/**
 * Contexto de Tenant (Empresa)
 * Gerencia estado do tenant atual e operações relacionadas
 * 
 * @author Equipe GestaoeCRM
 * @date 29/09/2025
 */

import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import api from '@/services/api';

// Tipos
export interface Tenant {
  id: string;
  cnpj: string;
  companyName: string;
  tradeName?: string;
  email: string;
  phone?: string;
  addressStreet?: string;
  addressNumber?: string;
  addressComplement?: string;
  addressNeighborhood?: string;
  addressCity?: string;
  addressState?: string;
  addressZipcode?: string;
  planType: 'FREE' | 'BASIC' | 'PROFESSIONAL' | 'ENTERPRISE';
  maxUsers: number;
  maxProjects: number;
  maxDemandsPerMonth: number;
  active: boolean;
  trialDays?: number;
  trialEndsAt?: string;
  subscriptionEndsAt?: string;
  createdAt: string;
  updatedAt: string;
  _count?: {
    users: number;
    projects: number;
    demands: number;
  };
}

export interface TenantStats {
  totalUsers: number;
  totalProjects: number;
  totalDemands: number;
  activeUsers: number;
  completedProjects: number;
  pendingDemands: number;
}

export interface TenantContextType {
  currentTenant: Tenant | null;
  tenants: Tenant[];
  loading: boolean;
  error: string | null;
  stats: TenantStats | null;
  // Ações
  setCurrentTenant: (tenant: Tenant | null) => void;
  fetchTenants: () => Promise<void>;
  fetchTenantStats: (tenantId: string) => Promise<void>;
  createTenant: (tenantData: Partial<Tenant>) => Promise<Tenant>;
  updateTenant: (tenantId: string, tenantData: Partial<Tenant>) => Promise<Tenant>;
  toggleTenantStatus: (tenantId: string) => Promise<void>;
  deleteTenant: (tenantId: string) => Promise<void>;
  refreshTenantData: () => Promise<void>;
}

// Context
const TenantContext = createContext<TenantContextType | undefined>(undefined);

// Provider
interface TenantProviderProps {
  children: ReactNode;
}

export function TenantProvider({ children }: TenantProviderProps) {
  const [currentTenant, setCurrentTenant] = useState<Tenant | null>(null);
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [stats, setStats] = useState<TenantStats | null>(null);
  const queryClient = useQueryClient();

  // Carregar tenant atual do localStorage ao inicializar
  useEffect(() => {
    const savedTenant = localStorage.getItem('currentTenant');
    if (savedTenant) {
      try {
        const tenant = JSON.parse(savedTenant);
        setCurrentTenant(tenant);
      } catch (error) {
        // console.warn('Erro ao carregar tenant do localStorage:', error);
        localStorage.removeItem('currentTenant');
      }
    }
  }, []);

  // Salvar tenant atual no localStorage quando mudar
  useEffect(() => {
    if (currentTenant) {
      localStorage.setItem('currentTenant', JSON.stringify(currentTenant));
    } else {
      localStorage.removeItem('currentTenant');
    }
  }, [currentTenant]);

  // Buscar lista de tenants (apenas para admin)
  const fetchTenants = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await api.get('/tenants');
      
      if (response.data.success) {
        setTenants(response.data.data);
      } else {
        setError('Erro ao carregar empresas');
      }
    } catch (err: any) {
      // console.error('Erro ao buscar tenants:', err);
      setError(err.response?.data?.message || 'Erro ao carregar empresas');
    } finally {
      setLoading(false);
    }
  };

  // Buscar estatísticas de um tenant
  const fetchTenantStats = async (tenantId: string) => {
    try {
      setLoading(true);
      setError(null);

      const response = await api.get(`/tenants/${tenantId}/stats`);
      
      if (response.data.success) {
        setStats(response.data.data);
      } else {
        setError('Erro ao carregar estatísticas');
      }
    } catch (err: any) {
      // console.error('Erro ao buscar estatísticas do tenant:', err);
      setError(err.response?.data?.message || 'Erro ao carregar estatísticas');
    } finally {
      setLoading(false);
    }
  };

  // Criar novo tenant
  const createTenant = async (tenantData: Partial<Tenant>): Promise<Tenant> => {
    try {
      setLoading(true);
      setError(null);

      const response = await api.post('/tenants', tenantData);
      
      if (response.data.success) {
        const newTenant = response.data.data;
        setTenants(prev => [newTenant, ...prev]);
        
        // Limpar cache do React Query
        queryClient.invalidateQueries({ queryKey: ['tenants'] });
        
        return newTenant;
      } else {
        throw new Error(response.data.message || 'Erro ao criar empresa');
      }
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || 'Erro ao criar empresa';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Atualizar tenant
  const updateTenant = async (tenantId: string, tenantData: Partial<Tenant>): Promise<Tenant> => {
    try {
      setLoading(true);
      setError(null);

      const response = await api.put(`/tenants/${tenantId}`, tenantData);
      
      if (response.data.success) {
        const updatedTenant = response.data.data;
        
        // Atualizar lista de tenants
        setTenants(prev => 
          prev.map(tenant => 
            tenant.id === tenantId ? updatedTenant : tenant
          )
        );
        
        // Se for o tenant atual, atualizar também
        if (currentTenant?.id === tenantId) {
          setCurrentTenant(updatedTenant);
        }
        
        // Limpar cache do React Query
        queryClient.invalidateQueries({ queryKey: ['tenants'] });
        queryClient.invalidateQueries({ queryKey: ['tenant', tenantId] });
        
        return updatedTenant;
      } else {
        throw new Error(response.data.message || 'Erro ao atualizar empresa');
      }
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || 'Erro ao atualizar empresa';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Ativar/Desativar tenant
  const toggleTenantStatus = async (tenantId: string) => {
    try {
      setLoading(true);
      setError(null);

      const response = await api.patch(`/tenants/${tenantId}/status`);
      
      if (response.data.success) {
        const updatedTenant = response.data.data;
        
        // Atualizar lista de tenants
        setTenants(prev => 
          prev.map(tenant => 
            tenant.id === tenantId ? updatedTenant : tenant
          )
        );
        
        // Se for o tenant atual, atualizar também
        if (currentTenant?.id === tenantId) {
          setCurrentTenant(updatedTenant);
        }
        
        // Limpar cache do React Query
        queryClient.invalidateQueries({ queryKey: ['tenants'] });
        queryClient.invalidateQueries({ queryKey: ['tenant', tenantId] });
      } else {
        throw new Error(response.data.message || 'Erro ao alterar status da empresa');
      }
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || 'Erro ao alterar status da empresa';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Deletar tenant
  const deleteTenant = async (tenantId: string) => {
    try {
      setLoading(true);
      setError(null);

      const response = await api.delete(`/tenants/${tenantId}`);
      
      if (response.data.success) {
        // Remover da lista de tenants
        setTenants(prev => prev.filter(tenant => tenant.id !== tenantId));
        
        // Se for o tenant atual, limpar
        if (currentTenant?.id === tenantId) {
          setCurrentTenant(null);
        }
        
        // Limpar cache do React Query
        queryClient.invalidateQueries({ queryKey: ['tenants'] });
        queryClient.invalidateQueries({ queryKey: ['tenant', tenantId] });
      } else {
        throw new Error(response.data.message || 'Erro ao deletar empresa');
      }
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || 'Erro ao deletar empresa';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Atualizar dados do tenant atual
  const refreshTenantData = async () => {
    if (currentTenant) {
      try {
        const response = await api.get(`/tenants/${currentTenant.id}`);
        if (response.data.success) {
          setCurrentTenant(response.data.data);
        }
      } catch (err) {
        // console.error('Erro ao atualizar dados do tenant:', err);
      }
    }
  };

  const value: TenantContextType = {
    currentTenant,
    tenants,
    loading,
    error,
    stats,
    setCurrentTenant,
    fetchTenants,
    fetchTenantStats,
    createTenant,
    updateTenant,
    toggleTenantStatus,
    deleteTenant,
    refreshTenantData,
  };

  return (
    <TenantContext.Provider value={value}>
      {children}
    </TenantContext.Provider>
  );
}

// Hook para usar o contexto
export function useTenant() {
  const context = useContext(TenantContext);
  if (context === undefined) {
    throw new Error('useTenant deve ser usado dentro de um TenantProvider');
  }
  return context;
}
